<template>
  <section v-if="!isPartnersHidden" class="property-info">
    <header class="property-info__head">
      <span class="property-info__title text-body-1 text-sans text-bold">
        {{ $t("unit_page.un_partners") }}
      </span>
    </header>

    <div class="property-info__partners">
      <div
        v-for="(item, idx) in oneFromToPartners"
        :key="`combined-${idx}`"
        class="card"
        @click="openPartner(item.link)"
      >
        <div class="logo-img">
          <img :src="item.logo || noIcon" alt="icon" >
        </div>
        <div class="partner-text-wrapper">
          <h4 class="text-body-1 text-bold mobile-font--b3">
            {{ item.name || "Not provided" }}
          </h4>
          <p class="text-body-2 text-regular text-black-monochrome-60">
            {{ item.sub }}
          </p>
        </div>
      </div>
      <div
        v-for="(partner, index) in props.partners ?? []"
        :key="`p-${index}`"
        class="card"
        @click="openPartner(partner.url)"
      >
        <div class="logo-img">
          <img :src="partner?.logoFile?.url || noIcon" alt="icon" >
        </div>
        <div class="partner-text-wrapper">
          <h4 class="text-body-1 text-bold mobile-font--b3">
            {{ partner.name || "Not provided" }}
          </h4>
          <p class="text-body-2 text-regular text-black-monochrome-60">
            {{ partner.subtitle }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Notification } from "~/services/notifications/toast";
import noIcon from "~/assets/icons/no-icon.svg";
import type {
  ContractResponseDTO,
  PartnerResponseDTO,
  ProviderResponseDTO,
  TokenomicsDTO,
  UnitHolderDTO,
  UnitFileDTO,
} from "~/services/swagger/Api";

interface Props {
  updateDate?: string;
  partners?: PartnerResponseDTO[] | null | undefined;
  provider?: ProviderResponseDTO;
  refLink?: string;
  externalUrl?: string;
  tokenomics?: TokenomicsDTO;
  holders: UnitHolderDTO[];
  contract?: ContractResponseDTO;
  images?: UnitFileDTO[];
}

const props = withDefaults(defineProps<Props>(), {
  updateDate: "two hours ago",
  partners: () => [],
});

const { t } = useI18n();

const oneFromToPartners = computed(() => {
  if (!props.contract?.issuer && !props.provider) return [];

  const buildPartner = (
    logo: string | undefined,
    link: string | undefined,
    name: string | undefined,
    sub: string
  ) => ({ logo, link, name, sub });

  const partners = [];

  if (props.contract?.issuer) {
    partners.push(
      buildPartner(
        props.contract?.issuer?.logoFile?.url,
        props.refLink ?? props.contract?.issuer.url,
        props.contract?.issuer.name,
        t("search_filters.fr_issuer")
      )
    );
  }

  if (
    props.provider &&
    !props.provider?.name.includes(
      props.contract?.issuer?.name || "notIncludes"
    )
  ) {
    partners.push(
      buildPartner(
        props.provider.logoFile?.url,
        props.provider.url,
        props.provider.name,
        t("search_filters.fr_issuer")
      )
    );
  }

  return partners;
});

const isPartnersHidden = computed(() => {
  return !(props.partners?.length || props.provider || props.contract?.issuer);
});

const openPartner = (link: string | undefined) => {
  if (!link) {
    const errorNotify = new Notification({
      type: "system",
      message: "We don't have a link to this partner for now =(",
    });
    errorNotify.createNewToast();
    return;
  }
  window.open(link, "_blank");
};
</script>

<style scoped lang="scss">
.property-info {
  width: 100%;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-card);

  &__head {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    color: var(--text-primary);
  }

  &__partners {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;

    .card {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      cursor: pointer;

      .logo-img {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        img {
          max-height: 40px;
          max-width: 40px;
          object-fit: cover;
        }
      }

      .partner-text-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        min-width: 0;

        h4 {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
      }
    }
  }
}

@media (max-width: 59rem) {
  @include mobile-fonts;

  .property-info {
    max-width: 345px;

    &__partners {
      .card {
        .logo-img {
          width: 32px;
          height: 32px;
        }
      }
    }
  }
}
</style>
